import React, { useEffect } from 'react';
import Header from '../components/Header';
import Hero from '../components/Hero';
import TrustIndicators from '../components/TrustIndicators';
import FabricShowcase from '../components/FabricShowcase';
import ProductGrid from '../components/ProductGrid';
import WhyChooseUs from '../components/WhyChooseUs';
import HowToOrder from '../components/HowToOrder';
import ContactSection from '../components/ContactSection';
import LeadForm from '../components/LeadForm';
import Footer from '../components/Footer';
import FloatingWhatsApp from '../components/FloatingWhatsApp';
import FabricGuide from '../components/FabricGuide';
import GSMGuide from '../components/GSMGuide';
import UseCaseFAQ from '../components/UseCaseFAQ';
import { seoKeywords, useCaseFAQ } from '../data/mockData';

const HomePage = () => {
  useEffect(() => {
    // Set page title and meta tags for SEO - Factual with broad keyword coverage
    document.title = 'ARIHANT SALES Jabalpur | Fabri Wholesaler in Madhya Pradesh (MP) | Best Wholesale Cloth Market';

    // Meta description - Factual with keyword-rich content
    const metaDescription = document.querySelector('meta[name="description"]');
    const descContent = 'ARIHANT SALES Jabalpur  Largest fabric wholesaler in Madhya Pradesh (MP). Wholesale supplier of Superpoly, 4-way, 2-way, Zurich, Hosiery & Sportswear fabrics. Best wholesale prices for garment manufacturers.';
    if (metaDescription) {
      metaDescription.setAttribute('content', descContent);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = descContent;
      document.head.appendChild(meta);
    }

    // Meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', seoKeywords.join(', '));
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = seoKeywords.join(', ');
      document.head.appendChild(meta);
    }

    // Open Graph tags for social sharing
    const ogTags = [
      { property: 'og:title', content: 'ARIHANT SALES - Fabric Wholesaler in Jabalpur, Madhya Pradesh' },
      { property: 'og:description', content: 'Largest fabric wholesaler in Jabalpur, MP. Wholesale supplier of sportswear, hosiery, and stretch fabrics at best prices.' },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: 'https://www.arihantsalesindia.in/' },
      { property: 'og:locale', content: 'en_IN' }
    ];

    ogTags.forEach(tag => {
      let element = document.querySelector(`meta[property="${tag.property}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', tag.property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', tag.content);
    });

    // Structured Data for Local Business
    const localBusinessData = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "ARIHANT SALES",
      "description": "Jabalpur's largest fabric wholesaler for MP. Sportswear, Hosiery & Zurich Fabrics.",
      "@id": "https://www.arihantsalesindia.in/",
      "url": "https://www.arihantsalesindia.in/",
      "image": "https://customer-assets.emergentagent.com/job_17e9985b-73e9-4734-bbfb-47e8a4cafda2/artifacts/k6189tfw_arihant%20sales.jpg",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "606, Garha Phatak, In front of Badi Mahakali Mandir",
        "addressLocality": "Jabalpur",
        "addressRegion": "Madhya Pradesh",
        "postalCode": "482002",
        "addressCountry": "IN"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "23.1744563",
        "longitude": "79.9283883"
      },
      "telephone": "+919713889720",
      "priceRange": "$$",
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
          "opens": "11:00",
          "closes": "21:00"
        }
      ]
    };

    // Organization Schema for Brand Authority
    const organizationData = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "ARIHANT SALES",
      "url": "https://www.arihantsalesindia.in",
      "logo": "https://customer-assets.emergentagent.com/job_17e9985b-73e9-4734-bbfb-47e8a4cafda2/artifacts/k6189tfw_arihant%20sales.jpg",
      "description": "Leading fabric wholesaler in Jabalpur, Madhya Pradesh. Supplying premium quality fabrics for sportswear, garments, and fashion across India.",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "606, Garha Phatak, In front of Badi Mahakali Mandir",
        "addressLocality": "Jabalpur",
        "addressRegion": "Madhya Pradesh",
        "postalCode": "482002",
        "addressCountry": "IN"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+91-9713889720",
        "contactType": "sales",
        "areaServed": "IN",
        "availableLanguage": ["Hindi", "English"]
      }
    };

    // Breadcrumb Schema
    const breadcrumbData = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [{
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.arihantsalesindia.in/"
      }]
    };

    // Product Schema for each item in mockData
    const productSchemaData = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "itemListElement": (require('../data/mockData').fabricProducts || []).map((product, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Product",
          "name": product.name,
          "description": product.description,
          "image": product.image.startsWith('http') ? product.image : `https://www.arihantsalesindia.in${product.image}`,
          "sku": `ARIHANT-${product.id}`,
          "url": `https://www.arihantsalesindia.in/#products`,
          "offers": {
            "@type": "Offer",
            "price": "1.00",
            "priceCurrency": "INR",
            "priceValidUntil": "2027-12-31",
            "availability": "https://schema.org/InStock",
            "url": "https://www.arihantsalesindia.in/#products",
            "shippingDetails": {
              "@type": "OfferShippingDetails",
              "shippingRate": {
                "@type": "MonetaryAmount",
                "value": "0",
                "currency": "INR"
              },
              "shippingDestination": {
                "@type": "DefinedRegion",
                "addressCountry": "IN"
              },
              "deliveryTime": {
                "@type": "ShippingDeliveryTime",
                "handlingTime": {
                  "@type": "QuantitativeValue",
                  "minValue": 0,
                  "maxValue": 1,
                  "unitCode": "DAY"
                },
                "transitTime": {
                  "@type": "QuantitativeValue",
                  "minValue": 1,
                  "maxValue": 5,
                  "unitCode": "DAY"
                }
              }
            },
            "hasMerchantReturnPolicy": {
              "@type": "MerchantReturnPolicy",
              "applicableCountry": "IN",
              "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnPeriod",
              "merchantReturnDays": 7,
              "returnMethod": "https://schema.org/ReturnByMail",
              "returnFees": "https://schema.org/FreeReturn"
            }
          },
          "brand": {
            "@type": "Brand",
            "name": "ARIHANT SALES"
          }
        }
      }))
    };

    const scriptLocal = document.createElement('script');
    scriptLocal.type = 'application/ld+json';
    scriptLocal.text = JSON.stringify(localBusinessData);
    document.head.appendChild(scriptLocal);

    const scriptProducts = document.createElement('script');
    scriptProducts.type = 'application/ld+json';
    scriptProducts.text = JSON.stringify(productSchemaData);
    document.head.appendChild(scriptProducts);

    // FAQ Schema for Rich Snippets
    const faqSchemaData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": useCaseFAQ.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };

    const scriptFAQ = document.createElement('script');
    scriptFAQ.type = 'application/ld+json';
    scriptFAQ.text = JSON.stringify(faqSchemaData);
    document.head.appendChild(scriptFAQ);

    // Organization Schema
    const scriptOrganization = document.createElement('script');
    scriptOrganization.type = 'application/ld+json';
    scriptOrganization.text = JSON.stringify(organizationData);
    document.head.appendChild(scriptOrganization);

    // Breadcrumb Schema
    const scriptBreadcrumb = document.createElement('script');
    scriptBreadcrumb.type = 'application/ld+json';
    scriptBreadcrumb.text = JSON.stringify(breadcrumbData);
    document.head.appendChild(scriptBreadcrumb);

    // Canonical Link - Hardcoded to primary domain to prevent duplicate indexing issues
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = 'https://www.arihantsalesindia.in/';

    return () => {
      // Cleanup
      if (scriptLocal.parentNode) scriptLocal.parentNode.removeChild(scriptLocal);
      if (scriptProducts.parentNode) scriptProducts.parentNode.removeChild(scriptProducts);
      if (scriptFAQ.parentNode) scriptFAQ.parentNode.removeChild(scriptFAQ);
      if (scriptOrganization.parentNode) scriptOrganization.parentNode.removeChild(scriptOrganization);
      if (scriptBreadcrumb.parentNode) scriptBreadcrumb.parentNode.removeChild(scriptBreadcrumb);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        <Hero />
        <FabricShowcase />
        <TrustIndicators />
        <ProductGrid />
        <WhyChooseUs />
        <GSMGuide />
        <HowToOrder />
        <FabricGuide />
        <UseCaseFAQ />
        <LeadForm />
        <ContactSection />
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
};

export default HomePage;
